import { useLiveRecipientDefaults } from "@/modules/billing/useLiveRecipientDefaults";
import { ChargeOnSendPicker } from "@/modules/billing/ChargeOnSendPicker";
import { recipientDefaultToList } from "@/modules/billing/syncRecipientDefault";
import { InvoiceSendStep } from "@/modules/billing/InvoiceSendStep";
import { useBillToContact } from "@/modules/billing/useBillToContact";
import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2, Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { InvoiceDocumentPreview } from "@/modules/billing/InvoiceDocumentPreview";
import { resolveInvoiceOrganizationName } from "@/modules/billing/invoiceEmailTemplate";
import {
  resolveInvoiceSendRecipientEmail,
  resolveInvoiceSendRecipientPhone,
} from "@/modules/billing/billingUtils";
import { getInvoiceOrganizationBranding } from "@/modules/billing/invoiceOrganizationInfo";
import type {
  ClientInvoice,
  ClientInvoiceLineItem,
  Ticket,
  TicketDeliverable,
} from "@/modules/types";
import {
  buildCombinedInvoicePropertySummary,
  getCombinedTicketIds,
  sortTicketsForCombinedInvoice,
} from "@/modules/tickets/combinedTicketInvoiceUtils";
import { buildTicketSendFooterSummary } from "@/modules/tickets/TicketInvoiceSendPreview";
import {
  buildTicketInvoiceSendEmailPreviews,
  clientInvoiceLineItemsToDrafts,
  DEFAULT_TICKET_PAYMENT_EMAIL_MESSAGE,
} from "@/modules/tickets/ticketInvoicePreview";
import {
  applyTicketEmailTemplate,
  buildTicketPaymentCopyFromDeliverables,
} from "@/modules/tickets/ticketInvoiceCopy";
import { useMessageTemplateEdits } from "@/modules/settings/messageTemplates/useMessageTemplateEdits";
import { resolveTicketBillingEmail } from "@/modules/tickets/ticketListMeta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type PreviewStep = "invoice" | "email";

type TicketCombinedInvoicePreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tickets: Ticket[];
  companyById: Map<string, Company>;
  contactById: Map<string, Contact>;
  selectedRecipientEmail?: string;
  onInvoiceSent?: () => void;
};

const StepIndicator = ({ step }: { step: PreviewStep }) => (
  <div className="flex items-center gap-2 pt-2 text-sm">
    <span
      className={cn(
        "font-medium",
        step === "invoice" ? "text-foreground" : "text-muted-foreground",
      )}
    >
      1. Invoice
    </span>
    <ChevronRight className="size-4 text-muted-foreground" />
    <span
      className={cn(
        "font-medium",
        step === "email" ? "text-foreground" : "text-muted-foreground",
      )}
    >
      2. Email
    </span>
  </div>
);

export const TicketCombinedInvoicePreviewDialog = ({
  open,
  onOpenChange,
  tickets,
  companyById,
  contactById,
  selectedRecipientEmail = "",
  onInvoiceSent,
}: TicketCombinedInvoicePreviewDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { title, companyLegalName } = useConfigurationContext();
  const [step, setStep] = useState<PreviewStep>("invoice");
  const [draftInvoice, setDraftInvoice] = useState<ClientInvoice | null>(null);
  const [lineItems, setLineItems] = useState<ClientInvoiceLineItem[]>([]);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState(
    DEFAULT_TICKET_PAYMENT_EMAIL_MESSAGE,
  );
  const [deliverySubject, setDeliverySubject] = useState("");
  const [serviceLines, setServiceLines] = useState<string[]>([]);
  const [phones, setPhones] = useState<string[]>([]);
  const [sendSms, setSendSms] = useState(false);
  /** Saved card / bank account to charge on send; null emails the invoice. */
  const [chargeMethodId, setChargeMethodId] = useState<string | null>(null);
  const sentRef = useRef(false);

  const recipientEmail = toEmails.join(", ");
  const phone = phones.join(", ");

  const sortedTickets = useMemo(
    () => sortTicketsForCombinedInvoice(tickets),
    [tickets],
  );
  const ticketIds = useMemo(
    () => getCombinedTicketIds(sortedTickets),
    [sortedTickets],
  );
  const { edits: templateEdits } = useMessageTemplateEdits();
  const primaryTicket = sortedTickets[0] ?? null;
  const company = primaryTicket?.company_id
    ? companyById.get(String(primaryTicket.company_id))
    : null;
  const contact = primaryTicket?.contact_id
    ? contactById.get(String(primaryTicket.contact_id))
    : null;
  // Bill To follows the chosen recipients (same rule as the send flow).
  const billToContact = useBillToContact({
    company,
    contact,
    recipients: toEmails,
  });

  const ticketIdFilter = ticketIds.length
    ? { "ticket_id@in": `(${ticketIds.join(",")})` }
    : undefined;

  const { data: deliverables = [] } = useGetList<TicketDeliverable>(
    "ticket_deliverables",
    {
      filter: ticketIdFilter,
      sort: { field: "sort_order", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: open && ticketIds.length > 0 },
  );

  useLiveRecipientDefaults({
    open,
    email: resolveInvoiceSendRecipientEmail({
      company,
      contact,
      ticketRequesterEmail: primaryTicket?.requester_email,
    }).toLowerCase(),
    phone: resolveInvoiceSendRecipientPhone({ company, contact }),
    setEmails: setToEmails,
    setPhones,
  });

  const organizationName = useMemo(
    () => resolveInvoiceOrganizationName({ title, companyLegalName }),
    [title, companyLegalName],
  );
  const invoiceBranding = useMemo(() => getInvoiceOrganizationBranding(), []);
  const propertySummary = useMemo(
    () => buildCombinedInvoicePropertySummary(sortedTickets),
    [sortedTickets],
  );

  const unbilledDeliverables = useMemo(
    () => deliverables.filter((file) => !file.invoiced_invoice_id),
    [deliverables],
  );

  const applyPaymentCopy = (items: TicketDeliverable[]) => {
    const addressByTicket = new Map(
      sortedTickets.map((ticket) => [
        String(ticket.id),
        ticket.subject?.trim() || `Ticket #${ticket.id}`,
      ]),
    );
    const addresses = new Set(addressByTicket.values());
    // One address per service line; the subject counts the properties
    // instead of joining every address.
    const subjectAddress =
      addresses.size > 1 ? `${addresses.size} properties` : propertySummary;
    const copy = applyTicketEmailTemplate(
      buildTicketPaymentCopyFromDeliverables(
        items,
        subjectAddress,
        (item) =>
          addressByTicket.get(String(item.ticket_id)) ?? propertySummary,
      ),
      "tickets.invoice.email",
      templateEdits.get("tickets.invoice.email"),
      {
        propertyAddress: subjectAddress,
        companyName: organizationName,
        clientFirstName: contact?.first_name,
      },
    );
    setSubject(copy.subject);
    setEmailMessage(copy.message);
    setDeliverySubject(copy.deliverySubject);
    setServiceLines(copy.serviceLines);
  };

  const prepareMutation = useMutation({
    mutationFn: () =>
      dataProvider.prepareCombinedTicketInvoice({
        ticketIds,
        baseUrl: window.location.origin,
        recipientEmail: selectedRecipientEmail,
      }),
    onSuccess: (data) => {
      setDraftInvoice(data.invoice as ClientInvoice);
      setLineItems((data.line_items ?? []) as ClientInvoiceLineItem[]);
      setPaymentUrl(data.payment_url);
      const preparedInvoice = data.invoice as ClientInvoice;
      const liveEmail = resolveInvoiceSendRecipientEmail({
        company,
        contact,
        ticketRequesterEmail:
          data.to ||
          (primaryTicket
            ? resolveTicketBillingEmail(primaryTicket, company, contact)
            : "") ||
          selectedRecipientEmail.trim(),
        fallbackEmail: preparedInvoice.recipient_email,
      });
      setToEmails(recipientDefaultToList(liveEmail.toLowerCase()));
      applyPaymentCopy(unbilledDeliverables);
      refresh();
    },
    onError: (error: Error) => {
      notify(error.message || "Could not prepare combined invoice", {
        type: "error",
      });
      onOpenChange(false);
    },
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      dataProvider.sendCombinedTicketInvoice({
        ticketIds,
        baseUrl: window.location.origin,
        message: emailMessage,
        subject: subject.trim(),
        smsTo: sendSms ? phone : undefined,
        sendSms,
        recipientEmail,
        chargePaymentMethodId: chargeMethodId,
      }),
    onSuccess: (data) => {
      sentRef.current = true;
      const charge = data?.charge;
      if (charge?.status === "failed") {
        notify(
          `Charge declined (${charge.message}) — invoice emailed with the payment link`,
          { type: "warning" },
        );
      } else {
        notify(
          charge?.status === "succeeded"
            ? "Paid — files and receipt sent to the client"
            : charge?.status === "processing"
              ? "Bank payment started — files sent; it clears in about 4 business days"
              : "Combined invoice sent — files will deliver after payment",
          { type: "success" },
        );
      }
      onInvoiceSent?.();
      onOpenChange(false);
      refresh();
    },
    onError: (error: Error) =>
      notify(error.message || "Could not send combined invoice", {
        type: "error",
      }),
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      dataProvider.cancelCombinedTicketInvoiceDraft({ ticketIds }),
  });

  useEffect(() => {
    if (!open) return;
    sentRef.current = false;
    setStep("invoice");
    setEmailMessage(DEFAULT_TICKET_PAYMENT_EMAIL_MESSAGE);
    setSubject("");
    setDeliverySubject("");
    setServiceLines([]);
    const formattedPhone = resolveInvoiceSendRecipientPhone({
      company,
      contact,
    });
    setPhones(recipientDefaultToList(formattedPhone));
    setSendSms(Boolean(formattedPhone.trim()));
    setChargeMethodId(null);
    setToEmails([]);
    setDraftInvoice(null);
    setLineItems([]);
    prepareMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prepare when dialog opens
  }, [open, ticketIds.join(","), selectedRecipientEmail]);

  const previewLines = useMemo(
    () => clientInvoiceLineItemsToDrafts(lineItems),
    [lineItems],
  );

  const sendPreviews = useMemo(
    () =>
      buildTicketInvoiceSendEmailPreviews({
        draftInvoice,
        paymentUrl,
        organizationName,
        emailMessage,
        serviceLines,
        propertyAddress: propertySummary,
        deliverables,
        contactFirstName: billToContact?.first_name,
      }),
    [
      billToContact?.first_name,
      deliverables,
      draftInvoice,
      emailMessage,
      organizationName,
      paymentUrl,
      propertySummary,
      serviceLines,
    ],
  );

  const {
    paymentEmailHtml,
    paymentSmsText,
    deliveryEmailHtml,
    amountFormatted,
    fileCount,
  } = sendPreviews;

  const footerSummary =
    draftInvoice && amountFormatted !== "—"
      ? buildTicketSendFooterSummary(amountFormatted, draftInvoice.due_date)
      : "";

  const handleClose = (next: boolean) => {
    if (!next && open && !sentRef.current && draftInvoice?.status === "draft") {
      void cancelMutation.mutateAsync().then(() => {
        refresh();
        setStep("invoice");
        onOpenChange(false);
      });
      return;
    }
    if (!next) setStep("invoice");
    onOpenChange(next);
  };

  const isLoading = prepareMutation.isPending || !draftInvoice;
  const canSend =
    toEmails.length > 0 &&
    Boolean(subject.trim()) &&
    Boolean(paymentUrl) &&
    (!sendSms || phones.length > 0);

  const dialogWidthClass =
    step === "invoice"
      ? "sm:max-w-[min(96vw,calc(210mm+5rem))]"
      : "sm:max-w-[min(96vw,72rem)]";

  const invoiceDocument = draftInvoice ? (
    <InvoiceDocumentPreview
      organizationName={organizationName}
      organizationWebsite={invoiceBranding.website}
      organizationAddress={invoiceBranding.address}
      invoiceNumber={draftInvoice.invoice_number}
      status="draft"
      issueDate={draftInvoice.issue_date}
      dueDate={draftInvoice.due_date}
      terms={draftInvoice.terms ?? "Due on receipt"}
      company={company}
      contact={billToContact}
      billToEmail={toEmails.join(", ")}
      lines={previewLines}
      subtotal={Number(draftInvoice.subtotal) || undefined}
      feeAmount={Number(draftInvoice.fee_amount) || undefined}
      total={Number(draftInvoice.amount) || 0}
      balanceDue={Number(draftInvoice.amount) || 0}
      termsAndConditions={draftInvoice.notes}
    />
  ) : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          "flex max-h-[min(92vh,900px)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0",
          dialogWidthClass,
        )}
      >
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {step === "invoice"
              ? "Review combined invoice"
              : "Send combined invoice"}
            {draftInvoice ? (
              <Badge
                variant="outline"
                className="font-mono text-xs font-normal"
              >
                {draftInvoice.invoice_number}
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {sortedTickets.length} tickets · one payment link ·{" "}
            {unbilledDeliverables.length} delivery file
            {unbilledDeliverables.length === 1 ? "" : "s"}
          </DialogDescription>
          <StepIndicator step={step} />
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 size-4 animate-spin" />
              Preparing combined invoice…
            </div>
          ) : step === "invoice" ? (
            invoiceDocument
          ) : (
            <InvoiceSendStep
              idPrefix="combined-invoice-send"
              organizationName={organizationName}
              amountFormatted={amountFormatted}
              dueDate={draftInvoice.due_date}
              deliverySummary={`${unbilledDeliverables.length} file${unbilledDeliverables.length === 1 ? "" : "s"} · ${sortedTickets.length} tickets`}
              company={company}
              contact={contact}
              toEmails={toEmails}
              onToEmailsChange={setToEmails}
              subject={subject}
              onSubjectChange={setSubject}
              emailMessage={emailMessage}
              onEmailMessageChange={setEmailMessage}
              paymentEmailHtml={paymentEmailHtml || null}
              deliverySubject={deliverySubject}
              deliveryEmailHtml={deliveryEmailHtml || null}
              fileCount={fileCount}
              sendSms={sendSms}
              onSendSmsChange={setSendSms}
              phones={phones}
              onPhonesChange={setPhones}
              smsText={paymentSmsText}
              invoiceDocument={invoiceDocument}
              paymentChoice={
                <ChargeOnSendPicker
                  companyId={primaryTicket?.company_id}
                  contactId={primaryTicket?.contact_id}
                  recipientEmail={toEmails[0] ?? null}
                  clientLabel={company?.name ?? undefined}
                  value={chargeMethodId}
                  onChange={setChargeMethodId}
                  disabled={sendMutation.isPending}
                />
              }
            />
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-between">
          {step === "invoice" ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleClose(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={() => setStep("email")}>
                Continue to email
                <ChevronRight className="size-4" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-left text-sm text-muted-foreground">
                {footerSummary || null}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStep("invoice")}
                >
                  <ChevronLeft className="size-4" />
                  Back
                </Button>
                <Button
                  type="button"
                  disabled={!canSend || sendMutation.isPending}
                  onClick={() => sendMutation.mutate()}
                >
                  {sendMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Send className="size-4" />
                  )}
                  {chargeMethodId ? "Charge and Send" : "Send invoice"}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
